/* eslint-disable no-restricted-syntax */
/* eslint-disable no-await-in-loop */
import React, {
  ChangeEvent,
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { FormHandles } from '@unform/core';
import { MdUpload } from 'react-icons/md';
import { IoMdClose } from 'react-icons/io';

import Swal from 'sweetalert2';
import { Container } from './styles';
import Input from '~/components/Input';
import api from '~/services/api';
import Loading from '~/components/Loading';

export interface IFile {
  id: number;
  archiveEvent_id: number;
  name: string;
  url: string;
}

interface IImageGallery {
  initialData?: IFile[];
  event_id: number;
  onChange(files: IFile[]): void;
}

export interface IImageGalleryHandle {
  submit: () => void;
}

const ImageGallery: ForwardRefRenderFunction<
  IImageGalleryHandle,
  IImageGallery
> = ({ initialData, event_id, onChange }, ref) => {
  const formRef = useRef<FormHandles>(null);
  const [filesInfo, setFilesInfo] = useState<File[]>([]);
  const [oldFilesInfo, setOldFilesInfo] = useState<IFile[]>([]);
  const [oldFilesInfoDeleted, setOldFilesInfoDeleted] = useState<IFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('Carregando...');
  const [inputKey, setInputKey] = useState(Date.now());

  useEffect(() => {
    if (initialData) {
      setOldFilesInfo(initialData);
    }
  }, [initialData]);

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { files: filesData } = event.target;
      if (filesData) {
        const filesArray = Array.from(filesData);
        if (
          filesArray.some(
            (file) =>
              file.type !== 'image/jpeg' &&
              file.type !== 'image/png' &&
              file.type !== 'image/svg+xml'
          )
        ) {
          Swal.fire({
            icon: 'error',
            iconColor: '#ff6900',
            confirmButtonColor: '#ff6900',
            title: 'Formato inválido',
            text: 'O arquivo deve ser do formato JPG, JPEG, PNG ou SVG',
          });
        } else if (filesArray.some((file) => file.size > 500000)) {
          Swal.fire({
            icon: 'error',
            iconColor: '#ff6900',
            confirmButtonColor: '#ff6900',
            title: 'Arquivo muito grande',
            text: 'O tamanho do arquivo deve ser menor que 500KB',
          });
        } else {
          setFilesInfo((prevFiles) => [...prevFiles, ...filesArray]);
          // setOldFilesInfo([] as IFile[]);
        }
      }

      setInputKey(Date.now());
    },
    []
  );

  const handleDeleteOldFile = useCallback((oldFile: IFile) => {
    setOldFilesInfo((oldState) =>
      oldState.filter((file) => file.id !== oldFile.id)
    );
    setOldFilesInfoDeleted((oldState) => [...oldState, oldFile]);
  }, []);

  const handleDelete = useCallback((indexToDelete) => {
    setFilesInfo((prevFiles) =>
      prevFiles.filter((file, index) => index !== indexToDelete)
    );
    setInputKey(Date.now());
  }, []);

  const handleDrop = useCallback((event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    const { files: filesData } = event.dataTransfer;
    if (filesData) {
      const filesArray = Array.from(filesData);
      if (
        filesArray.some(
          (file) =>
            file.type !== 'image/jpeg' &&
            file.type !== 'image/png' &&
            file.type !== 'image/svg+xml'
        )
      ) {
        Swal.fire({
          icon: 'error',
          iconColor: '#ff6900',
          confirmButtonColor: '#ff6900',
          title: 'Formato inválido',
          text: 'O arquivo deve ser do formato JPG, JPEG, PNG ou SVG',
        });
      } else if (filesArray.some((file) => file.size > 500000)) {
        Swal.fire({
          icon: 'error',
          iconColor: '#ff6900',
          confirmButtonColor: '#ff6900',
          title: 'Arquivo muito grande',
          text: 'O tamanho do arquivo deve ser menor que 500KB',
        });
      } else {
        setFilesInfo((prevFiles) => [...prevFiles, ...filesArray]);
        // setOldFilesInfo([] as IFile[]);
      }
    }
    setInputKey(Date.now());
  }, []);

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLLabelElement>) => {
      event.preventDefault();
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    if (loading) return;
    try {
      setLoading(true);
      setLoadingText('Salvando...');
      let newFilesInfo: IFile[] = [];

      if (oldFilesInfoDeleted.length > 0) {
        for (const file of oldFilesInfoDeleted) {
          await api.delete(`archives-events/${file.archiveEvent_id}`);
        }
      }

      if (filesInfo.length > 0) {
        for (const file of filesInfo) {
          const fileFormData = new FormData();
          fileFormData.append('file', file);
          fileFormData.append('folder', 'events');

          const responseFile = await api.post('archives', fileFormData);

          const fileEventFormData = {
            archive_id: responseFile.data.id,
            event_id,
          };

          const responseFileEvent = await api.post(
            'archives-events',
            fileEventFormData
          );

          newFilesInfo.push({
            id: responseFile.data.id,
            archiveEvent_id: responseFileEvent.data.id,
            name: responseFile.data.name,
            url: responseFile.data.archive_url,
          });
        }
      }

      const element = document.getElementById('fileInput') as HTMLInputElement;

      if (element) {
        element.value = '';
      }

      newFilesInfo = [...newFilesInfo, ...oldFilesInfo];
      setFilesInfo([]);
      setOldFilesInfoDeleted([]);
      setOldFilesInfo(newFilesInfo);
      onChange(newFilesInfo);
    } catch (error) {
      Swal.fire('Oops...', 'Erro ao salvar as imagens!');
    } finally {
      setLoading(false);
      setLoadingText('Carregando...');
    }
  }, [
    event_id,
    filesInfo,
    loading,
    oldFilesInfo,
    oldFilesInfoDeleted,
    onChange,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      submit: () => {
        formRef.current?.submitForm();
      },
    }),
    []
  );

  return (
    <Container ref={formRef} onSubmit={handleSubmit} className="row">
      <div className="col-12">
        <h2 className="h4 fw-semibold text-center mt-5 mb-4">
          2- Galeria de imagens
        </h2>
        <p className="mb-4">
          Para uma melhor experiência para os nossos usuários, padronizamos os
          tamanhos das imagens que podem ser inseridas na plataforma. Saiba mais
          sobre o formato e o tamanho de cada imagem aqui antes de enviar suas
          imagens.
        </p>
      </div>
      <div className="col-12">
        <h3 className="h6 fw-normal">Galeria de fotos</h3>
        <Input
          key={inputKey}
          id="fileInput"
          type="file"
          multiple
          name="files"
          className="d-none"
          onChange={handleFileChange}
        />
        <label
          htmlFor="fileInput"
          className="w-100 text-center"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <MdUpload size={48} color="#FF6900" />
          <span className="d-block">
            Arraste e solte os arquivos aqui para
            <br />
            adicionar uma imagem
          </span>
          <span className="d-inline-flex my-2">Selecionar Imagens</span>
          <span className="d-block">Extensões aceitas: jpg, png, jpeg.</span>
          <span className="d-block text-danger mt-4">
            **Para uma melhor visualização das imagens do seu evento,
            recomendamos que as imagens tenham uma resolução de 575x350**
          </span>
        </label>

        {filesInfo.length > 1 && (
          <span className="d-block text-center mt-4">
            A primeira imagem abaixo será a imagem de capa do evento
          </span>
        )}

        {oldFilesInfo.map((file) => (
          <div
            key={file.id}
            className="d-flex justify-content-between arquives p-3 mt-3"
          >
            <div className="d-flex align-items-center">
              <img src={file.url} alt={file.name} />
              <p className="ms-3 mb-0">{file.name}</p>
            </div>
            <button
              type="button"
              className="bg-transparent border-0"
              onClick={() => handleDeleteOldFile(file)}
            >
              <IoMdClose size={24} color="#8C8C8C" />
            </button>
          </div>
        ))}

        {filesInfo.map((file, index) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className="d-flex justify-content-between arquives p-3 mt-3"
          >
            <div className="d-flex align-items-center">
              <img src={URL.createObjectURL(file)} alt={file.name} />
              <p className="ms-3 mb-0">{file.name}</p>
            </div>
            <button
              type="button"
              className="bg-transparent border-0"
              onClick={() => handleDelete(index)}
            >
              <IoMdClose size={24} color="#8C8C8C" />
            </button>
          </div>
        ))}
      </div>
      <Loading active={loading} text={loadingText} />
    </Container>
  );
};

export default forwardRef(ImageGallery);
